"use client";

import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function TechnicalOverview() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stats = [{
    label: "ESTABLISHED",
    value: "SINCE 1998"
  }, {
    label: "EXPERTS",
    value: "12"
  }, {
    label: "PATENTS",
    value: "45"
  }];
  return <section data-section-id="1029" id="technical-overview" className="bg-background text-foreground py-24 md:py-44 relative overflow-hidden border-t border-border">
      {}
      <div className="absolute top-0 left-0 right-0 h-px bg-border" aria-hidden="true" />

      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0 items-center">
          {}
          <div ref={imageRef} data-index={0} className={`lg:col-span-6 relative lg:-ml-12 transition-all duration-[800ms] ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <div className="relative px-5 lg:px-0">
              {}
              <div className="relative aspect-[16/9] lg:aspect-[4/5] overflow-hidden rounded-lg shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineering_lab_facility.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineering_lab_facility.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engineering_lab_facility.png-wvc-sizes" alt="Engineers inspecting high-precision components in the laboratory" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                {}
                <div className="absolute inset-0 pointer-events-none" style={{
                background: "radial-gradient(ellipse at center, transparent 40%, hsla(216, 50%, 12%, 0.6) 100%)"
              }} aria-hidden="true" />

                {}
                <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-primary" aria-hidden="true" />
                <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-primary" aria-hidden="true" />
                <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-primary" aria-hidden="true" />
                <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-primary" aria-hidden="true" />
              </div>
            </div>
          </div>

          {}
          <div ref={headingRef} className="lg:col-span-6 px-5 lg:pl-20 lg:pr-12 relative">
            {}
            <div className="absolute right-4 bottom-0 lg:right-12 pointer-events-none select-none font-default italic font-light leading-none" style={{
            fontSize: "clamp(8rem, 18vw, 16rem)",
            color: "var(--foreground)",
            opacity: 0.03
          }} aria-hidden="true">
              01
            </div>

            {}
            <div className={`font-mono uppercase mb-12 lg:mb-16 transition-all duration-[800ms] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(0.6875rem, 0.8vw, 0.8125rem)",
            letterSpacing: "0.22em",
            color: "var(--primary)",
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
              01 / § TECH — PRECISION ENGINEERING
            </div>

            {}
            <h2 className={`font-default font-bold text-foreground relative transition-all duration-[800ms] delay-[80ms] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2.25rem, 5vw, 4.25rem)",
            letterSpacing: "-0.02em",
            lineHeight: "1.02",
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
              Decades of <span className="text-primary">innovation</span>, one standard
            </h2>

            {}
            <div className={`relative my-10 lg:my-12 transition-all duration-[900ms] delay-[160ms] ${headingVisible ? "opacity-100" : "opacity-0"}`} style={{
            width: "200px"
          }}>
              <div className="h-px w-full bg-border" />
              <span className="absolute -right-0 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" aria-hidden="true" />
            </div>

            {}
            <p className={`font-default font-normal max-w-[480px] transition-all duration-[800ms] delay-[240ms] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
            lineHeight: "1.6",
            color: "var(--card-foreground)",
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
              Our engineering team leverages over twenty years of industrial expertise to deliver high-performance solutions. We maintain rigorous quality control protocols, ensuring every component meets global aerospace standards before leaving our facility.
            </p>

            {}
            <div className={`flex flex-wrap items-center gap-x-6 gap-y-3 mt-12 lg:mt-14 transition-all duration-[800ms] delay-[320ms] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
              {stats.map((stat, i) => <div data-index={i} key={stat.label} className="flex items-center gap-x-6">
                  <div className="font-mono uppercase whitespace-nowrap" style={{
                fontSize: "11px",
                letterSpacing: "0.22em",
                color: "var(--card-foreground)"
              }}>
                    <span>{stat.label}</span>
                    <span className="mx-2 text-border">·</span>
                    <span className="text-primary">{stat.value}</span>
                  </div>
                  {i < stats.length - 1 && <span className="hidden sm:inline-block h-px w-6 bg-border" aria-hidden="true" />}
                </div>)}
            </div>

            {}
            <div className={`mt-14 lg:mt-16 flex lg:justify-end transition-all duration-[800ms] delay-[400ms] ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
              <Link to="/CapabilitiesPage" className="group inline-flex items-center gap-2 font-default uppercase text-sm transition-colors duration-200 text-primary hover:text-white" style={{
              letterSpacing: "0.08em",
              textDecorationLine: "underline",
              textUnderlineOffset: "6px",
              textDecorationThickness: "1px"
            }}>
                <span>View our capabilities</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}